/**
 * Ensures the given outlet's PowerSync database is active and connected.
 *
 * Single source of truth for "make sure sync is actually working" — used
 * right after login, from the manual Reconnect button, the automatic
 * offline retry, and the Help menu's "Resync Now" — so every entry point
 * resolves the outlet the same way instead of duplicating retry/fallback
 * logic. On Electron this activates (or reconnects) the outlet in the main
 * process, retrying a few times if the IPC call fails — a single
 * dropped/failed attempt must never be the reason sync never starts. On the
 * web build there is no per-outlet main-process registry to activate, so it
 * just (re)connects the existing @powersync/web instance directly.
 */

import { supabase } from '@/renderer/services/supabase';
import { isElectron } from '@/renderer/util';

import { db, tryRecoverNativeEngine } from './database';

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1500;

// Re-relays the renderer's current Supabase session to the main process.
//
// Main authenticates PowerSync with whatever access token the renderer last
// relayed (see main/supabaseClient.ts applyRelayedSession + connector
// fetchCredentials). App.tsx relays on auth *events*, but if that initial relay
// is ever missed — or main simply hasn't received it yet when the user hits
// Reconnect — nothing else re-sends it, so main is stuck forever with no token
// ("[PowerSync] fetchCredentials: no relayed session yet"). Re-pushing here
// means every ensure-sync entry point (startup, login, Reconnect, the 30s
// offline auto-retry, "Resync Now") re-establishes main's credentials, not just
// the outlet context. Fire-and-forget: main applies the session synchronously
// on receipt and PowerSync retries fetchCredentials until the token lands.
export async function pushCurrentSessionToMain(): Promise<void> {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return;
    window.electron.auth.pushSession({
      event: 'TOKEN_REFRESHED',
      session: {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
        expires_at: session.expires_at ?? null,
        user_id: session.user.id,
      },
    });
  } catch (err) {
    console.error('[PowerSync] Failed to re-push session to main:', err);
  }
}

function activateOutlet(outletId: string, attemptNumber: number): void {
  window.electron.auth.pushOutletContext(outletId).catch((err) => {
    console.error(
      `[PowerSync] Failed to activate outlet (attempt ${attemptNumber}/${MAX_ATTEMPTS}):`,
      err,
    );
    if (attemptNumber < MAX_ATTEMPTS) {
      setTimeout(
        () => activateOutlet(outletId, attemptNumber + 1),
        RETRY_DELAY_MS,
      );
    }
  });
}

export function ensureOutletSync(outletId: string | null | undefined): void {
  // Attempt recovery back to the native engine first (no-op unless this
  // device is currently stranded on the @powersync/web fallback) — see
  // tryRecoverNativeEngine()'s docstring for why this matters: a repeated
  // stream-level sync error (fixable server-side) previously triggered the
  // same permanent fallback as a genuinely broken native engine, and nothing
  // ever un-stuck it short of an app version bump.
  tryRecoverNativeEngine();

  if (isElectron() && outletId) {
    // Re-establish credentials first, then (re)activate the outlet, so the
    // common case connects on the very first attempt. Order isn't strictly
    // required — main persists the relayed token independently of the outlet
    // and PowerSync retries fetchCredentials — but sending the session before
    // activation avoids a spurious "no relayed session yet" on connect.
    pushCurrentSessionToMain()
      .finally(() => activateOutlet(outletId, 1))
      .catch(() => {
        /* pushCurrentSessionToMain never rejects; keeps the linter happy */
      });
    return;
  }
  db.connect().catch((err) =>
    console.error('[PowerSync] connect failed:', err),
  );
}
